import type { ReactNode } from 'react';

// Small pieces shared by the API-key usage breakdown and request log, styled
// to match the expanded key panel in ApiKeyManagement.tsx.

export const sectionHeadingClass =
  'text-[11px] font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400';

export const tableWrapClass =
  'overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700';

export const tableClass = 'min-w-full text-xs';

export const theadClass =
  'bg-gray-50 dark:bg-gray-800/40 text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400';

export const thClass = 'px-3 py-2 text-left font-medium whitespace-nowrap';

export const tdClass = 'px-3 py-2 align-top text-gray-700 dark:text-gray-300';

export const rowClass = 'border-t border-gray-100 dark:border-gray-800';

/** Boxed message used for the empty / unavailable state of a section. */
export function SectionMessage({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-lg bg-gray-50 dark:bg-gray-800/40 px-3 py-4 text-center text-sm text-gray-500 dark:text-gray-400">
      {children}
    </div>
  );
}

export type StatusClass = 'success' | 'redirect' | 'client-error' | 'server-error' | 'other';

export function getStatusClass(statusCode: number): StatusClass {
  if (statusCode >= 500) return 'server-error';
  if (statusCode >= 400) return 'client-error';
  if (statusCode >= 300) return 'redirect';
  if (statusCode >= 200) return 'success';
  return 'other';
}

const STATUS_BADGE_CLASS: Record<StatusClass, string> = {
  success: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  redirect: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  'client-error': 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  'server-error': 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  other: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
};

/** HTTP status, coloured by class: 2xx green, 3xx blue, 4xx amber, 5xx red. */
export function StatusBadge({ statusCode }: { statusCode: number }) {
  const statusClass = getStatusClass(statusCode);
  return (
    <span
      data-status-class={statusClass}
      className={`inline-flex items-center rounded-md px-1.5 py-0.5 font-mono text-[11px] font-semibold tabular-nums ${STATUS_BADGE_CLASS[statusClass]}`}
    >
      {statusCode}
    </span>
  );
}
